/**
 * Utility functions for handling files with version support
 */

/**
 * Get the appropriate file URL based on version
 * @param {Object|string} file - File object or string
 * @param {string} version - Version to retrieve (preview, thumbnail, original)
 * @returns {string|null} File key or URL
 */
export const getFileUrl = (file, version = 'preview') => {
  if (!file) return null;
  
  // Handle string input (legacy support)
  if (typeof file === 'string') {
    return file;
  }
  
  // Handle file object with versions
  if (typeof file === 'object') {
    // Check if requested version exists
    if (version !== 'original' && file.versions && file.versions[version]) {
      return file.versions[version].key || file.versions[version].url;
    }
    
    // Fallback to original
    return file.key || file.url;
  }
  
  return null;
};

/**
 * Check if file is an image
 * @param {Object|string} file - File object
 * @returns {boolean}
 */
export const isImageFile = (file) => {
  if (!file) return false;
  
  let mimetype;
  
  if (typeof file === 'object') {
    mimetype = file.mimetype || file.mimeType || file.type;
  }
  
  return mimetype && mimetype.startsWith('image/');
};

/**
 * Get file extension from filename
 * @param {string} filename - File name
 * @returns {string} Extension without dot
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(lastDot + 1).toLowerCase() : '';
};

/**
 * Format file size to human readable
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get appropriate version based on context
 * @param {string} context - Usage context (avatar, thumbnail, preview, full)
 * @returns {string} Version name
 */
export const getVersionForContext = (context) => {
  const versionMap = {
    avatar: 'thumbnail',
    thumbnail: 'thumbnail',
    preview: 'preview',
    full: 'original',
    download: 'original'
  };
  
  return versionMap[context] || 'preview';
};

/**
 * Check if file has processed versions
 * @param {Object} file - File object
 * @returns {boolean}
 */
export const hasVersions = (file) => {
  return file && 
         typeof file === 'object' && 
         file.versions && 
         Object.keys(file.versions).length > 0;
};

/**
 * Get all available versions for a file
 * @param {Object} file - File object
 * @returns {string[]} Array of version names
 */
export const getAvailableVersions = (file) => {
  if (!hasVersions(file)) return ['original'];
  
  const versions = ['original'];
  if (file.versions) {
    versions.push(...Object.keys(file.versions));
  }
  
  return versions;
};

/**
 * Select best available version for context
 * @param {Object} file - File object
 * @param {string} preferredVersion - Preferred version
 * @returns {Object} Selected version data
 */
export const selectBestVersion = (file, preferredVersion = 'preview') => {
  if (!file) return null;
  
  // If file has the preferred version, use it
  if (hasVersions(file) && file.versions[preferredVersion]) {
    return {
      key: file.versions[preferredVersion].key,
      url: file.versions[preferredVersion].url,
      size: file.versions[preferredVersion].size,
      version: null // Don't pass version param when using versioned key
    };
  }
  
  // Fallback hierarchy
  const fallbackOrder = ['preview', 'thumbnail', 'original'];
  
  for (const version of fallbackOrder) {
    if (version === 'original') {
      return {
        key: file.key,
        url: file.url,
        size: file.size || file.fileSize,
        version: 'original'
      };
    }
    
    if (hasVersions(file) && file.versions[version]) {
      return {
        key: file.versions[version].key,
        url: file.versions[version].url,
        size: file.versions[version].size,
        version: null // Don't pass version param when using versioned key
      };
    }
  }
  
  // Last resort - return whatever we have
  return {
    key: file.key || file,
    url: file.url,
    size: file.size || file.fileSize,
    version: 'original'
  };
};